import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import { PodcastProgressBar } from "@memorang/ui/components/PodcastProgressBar";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { BlurView } from "expo-blur";
import { ImageBackground } from "expo-image";
import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Animated,
	PanResponder,
	Pressable,
	StyleSheet,
	useWindowDimensions,
} from "react-native";
import type { LayoutChangeEvent } from "react-native";
import { IconButton, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import usePodcastPlayer from "../hooks/usePodcastPlayer";
import { usePodcastPlayerStore } from "../hooks/usePodcastPlayerStore";
import { formatPodcastDuration } from "../utils";

const EXPANDED_PLAYER_HEIGHT = 140;
const INITIAL_PLAYER_WIDTH = 240;
const SNAP_MARGIN = 20; // Distance from edge to trigger snap
const EDGE_MARGIN = 16; // Inset from screen edge for the initial seed position
// Only reserved on native mobile where AppBottomTabs is rendered. Web and
// mobile web use AppDrawer instead, so there is no bottom tab bar to avoid.
const BOTTOM_TAB_APPROXIMATE_HEIGHT = 70;
// Web has no safe-area insets and no tab bar, so reserve a little breathing
// room on the right and bottom edges for both the initial seed position and
// drag/snap bounds so the player never hugs the viewport corners.
const WEB_EDGE_PADDING = 24;

// Floating draggable audio player component
export const FloatingPodcastPlayer = () => {
	const visible = usePodcastPlayerStore((store) => store.visible);
	const currentTrackInfo = usePodcastPlayerStore(
		(store) => store.currentTrackInfo,
	);
	const playerPosition = usePodcastPlayerStore((store) => store.playerPosition);
	const updatePlayerPosition = usePodcastPlayerStore(
		(store) => store.updatePlayerPosition,
	);
	const endOfQueue = usePodcastPlayerStore((store) => store.endOfQueue);
	const isFirstTrack = usePodcastPlayerStore((store) => store.isFirstTrack);

	const {
		isPlaying,
		playPause,
		skipToPrevious,
		skipToNext,
		seekTo,
		dismissPlayer,
		progress,
	} = usePodcastPlayer();
	const theme = useAppTheme();
	const router = useRouter();
	const { isMobile } = useDevice();
	const { width: screenWidth, height: screenHeight } = useWindowDimensions();
	const insets = useSafeAreaInsets();

	const handleOpenDetails = useCallback(() => {
		if (currentTrackInfo?.album) {
			router.push(`/podcasts/${currentTrackInfo.album}`);
		}
	}, [router, currentTrackInfo?.album]);

	const bottomReservedHeight = useMemo(
		() =>
			(isMobile && !isWeb ? BOTTOM_TAB_APPROXIMATE_HEIGHT : 0) +
			insets.bottom +
			(isWeb ? WEB_EDGE_PADDING : 0),
		[isMobile, insets.bottom],
	);

	const rightReservedWidth = isWeb ? WEB_EDGE_PADDING : 0;

	const [playerWidth, setPlayerWidth] = useState(INITIAL_PLAYER_WIDTH);

	// Seed position used when the user has not dragged the player yet. Anchors
	// it near the bottom-right, clear of the tab bar (native) or drawer edge
	// (web), and honoring the top safe-area inset.
	const initialPosition = useMemo(
		() => ({
			x: Math.max(
				0,
				screenWidth - playerWidth - Math.max(EDGE_MARGIN, rightReservedWidth),
			),
			y: Math.max(
				insets.top,
				screenHeight - EXPANDED_PLAYER_HEIGHT - bottomReservedHeight,
			),
		}),
		[
			screenWidth,
			screenHeight,
			playerWidth,
			insets.top,
			bottomReservedHeight,
			rightReservedWidth,
		],
	);

	const [position, setPosition] = useState(
		() => playerPosition ?? initialPosition,
	);

	const positionRef = useRef(position);
	useEffect(() => {
		positionRef.current = position;
	}, [position]);

	const startPositionRef = useRef(position);

	// Sync local state when the store position changes (e.g. user dragged), or
	// when the seed moves because window/insets changed and the user has not
	// dragged yet.
	useEffect(() => {
		setPosition(playerPosition ?? initialPosition);
	}, [playerPosition, initialPosition]);

	const handlePlayerLayout = useCallback((e: LayoutChangeEvent) => {
		setPlayerWidth(e.nativeEvent.layout.width);
	}, []);

	const getBounds = useCallback(
		(x: number, y: number) => {
			const playerHeight = EXPANDED_PLAYER_HEIGHT;
			const boundedX = Math.max(
				0,
				Math.min(x, screenWidth - playerWidth - rightReservedWidth),
			);
			const minY = insets.top;
			const maxY = screenHeight - playerHeight - bottomReservedHeight;
			const boundedY = Math.max(minY, Math.min(y, maxY));
			return { boundedX, boundedY };
		},
		[
			playerWidth,
			bottomReservedHeight,
			rightReservedWidth,
			screenWidth,
			screenHeight,
			insets.top,
		],
	);

	const getSnappedPosition = useCallback(
		(x: number, y: number) => {
			let snappedX = x;
			let snappedY = y;
			if (x < SNAP_MARGIN) {
				snappedX = 0;
			} else if (
				x >
				screenWidth - playerWidth - rightReservedWidth - SNAP_MARGIN
			) {
				snappedX = screenWidth - playerWidth - rightReservedWidth;
			}
			const playerHeight = EXPANDED_PLAYER_HEIGHT;
			const topSnapThreshold = insets.top + SNAP_MARGIN;
			const bottomSnapThreshold =
				screenHeight - playerHeight - bottomReservedHeight - SNAP_MARGIN;
			if (y < topSnapThreshold) {
				snappedY = insets.top;
			} else if (y > bottomSnapThreshold) {
				snappedY = screenHeight - playerHeight - bottomReservedHeight;
			}
			return { x: snappedX, y: snappedY };
		},
		[
			playerWidth,
			bottomReservedHeight,
			rightReservedWidth,
			screenWidth,
			screenHeight,
			insets.top,
		],
	);

	const panResponder = useRef(
		PanResponder.create({
			onStartShouldSetPanResponder: () => true,
			onMoveShouldSetPanResponder: (_, gestureState) =>
				Math.abs(gestureState.dx) > 3 || Math.abs(gestureState.dy) > 3,

			onPanResponderGrant: () => {
				startPositionRef.current = { ...positionRef.current };
			},

			onPanResponderMove: (_, gestureState) => {
				const newX = startPositionRef.current.x + gestureState.dx;
				const newY = startPositionRef.current.y + gestureState.dy;
				const { boundedX, boundedY } = getBounds(newX, newY);
				setPosition({ x: boundedX, y: boundedY });
			},

			onPanResponderRelease: (_, gestureState) => {
				const newX = startPositionRef.current.x + gestureState.dx;
				const newY = startPositionRef.current.y + gestureState.dy;
				const { boundedX, boundedY } = getBounds(newX, newY);
				const { x: snappedX, y: snappedY } = getSnappedPosition(
					boundedX,
					boundedY,
				);
				setPosition({ x: snappedX, y: snappedY });
				updatePlayerPosition({ x: snappedX, y: snappedY });
			},
		}),
	).current;

	if (!(visible && currentTrackInfo)) {
		return null;
	}

	return (
		<Animated.View
			{...panResponder.panHandlers}
			onLayout={handlePlayerLayout}
			style={[
				styles.floatingPlayer,
				{
					transform: [{ translateX: position.x }, { translateY: position.y }],
					height: EXPANDED_PLAYER_HEIGHT,
					width: playerWidth,
				},
			]}
		>
			<ImageBackground
				source={{ uri: currentTrackInfo.artwork }}
				style={{ width: "100%", height: "100%" }}
				imageStyle={{ borderRadius: 16 }}
			>
				<BlurView
					intensity={50}
					tint="dark"
					style={{ flex: 1 }}
					experimentalBlurMethod="dimezisBlurView"
				>
					<Box gap={8} paddingHorizontal={16}>
						<Box>
							<PodcastProgressBar
								position={progress.position}
								duration={progress.duration}
								onSeek={seekTo}
							/>
							<Box
								flexDirection="row"
								justifyContent="space-between"
								width="100%"
							>
								<Text
									selectable={false}
									variant="bodySmall"
									style={{ color: theme.colors.white }}
								>
									{formatPodcastDuration(progress.position)}
								</Text>
								<Text
									selectable={false}
									variant="bodySmall"
									style={{ color: theme.colors.white }}
								>
									{formatPodcastDuration(progress.duration)}
								</Text>
							</Box>
						</Box>
						<Box gap={8}>
							<Pressable
								onPress={handleOpenDetails}
								disabled={!currentTrackInfo.album}
								accessibilityRole="link"
								accessibilityLabel={`Open details for ${currentTrackInfo.title}`}
							>
								<Text
									selectable={false}
									variant="titleSmall"
									numberOfLines={1}
									style={{ color: theme.colors.white }}
								>
									{currentTrackInfo.title}
								</Text>
								<Text
									selectable={false}
									variant="labelSmall"
									numberOfLines={1}
									style={{ color: "#ffffffce" }}
								>
									{currentTrackInfo.artist}
								</Text>
							</Pressable>
							<Box
								flexDirection="row"
								alignItems="center"
								justifyContent="center"
								gap={8}
							>
								<IconButton
									icon="skip-previous"
									containerColor={theme.colors.white}
									disabled={isFirstTrack}
									size={16}
									onPress={skipToPrevious}
									style={{ margin: 0 }}
								/>
								<IconButton
									icon={isPlaying ? "pause" : "play"}
									containerColor={theme.colors.white}
									size={16}
									onPress={playPause}
									style={{ margin: 0 }}
								/>
								<IconButton
									icon="skip-next"
									containerColor={theme.colors.white}
									disabled={endOfQueue}
									size={16}
									onPress={skipToNext}
									style={{ margin: 0 }}
								/>
								<IconButton
									iconColor={theme.colors.white}
									icon="close"
									size={16}
									onPress={dismissPlayer}
									style={{ margin: 0 }}
								/>
							</Box>
						</Box>
					</Box>
				</BlurView>
			</ImageBackground>
		</Animated.View>
	);
};

const styles = StyleSheet.create({
	floatingPlayer: {
		position: "absolute",
		left: 0,
		top: 0,
		width: 240,
		borderRadius: 16,
		overflow: "hidden",
		zIndex: 1000,
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 3 },
		shadowOpacity: 0.3,
		shadowRadius: 4,
		elevation: 6,
	},
});
